import React from 'react';
import { CheckCircle, Rocket, BookOpen, Zap } from 'lucide-react';
import { TIER_PRICING, TIER_INFO, MembershipTier } from '../../types/membership';

interface UpsellPricingProps {
  onNavigate: (page: string) => void;
}

const tierIcons: Record<MembershipTier, React.ReactNode> = {
  guest: null,
  starter: <Rocket className="w-6 h-6 text-white" />,
  reader: <BookOpen className="w-6 h-6 text-white" />,
  ultra: <Zap className="w-6 h-6 text-white" />,
};

const tierIconBg: Record<MembershipTier, string> = {
  guest: '',
  starter: 'bg-gradient-to-br from-green-500 to-emerald-600',
  reader: 'bg-gradient-to-br from-blue-500 to-indigo-600',
  ultra: 'bg-gradient-to-br from-purple-500 to-pink-600',
};

const tierFeatures: Record<string, string[]> = {
  starter: [
    'Unlimited stories',
    'Upload text files',
    'Progress saving',
    'Basic analytics',
    'Session history',
  ],
  reader: [
    'Everything in Starter',
    'Upload epub files',
    '100+ achievements + XP system',
    'Streak tracking + multipliers',
    'Full analytics + export',
    'Priority support',
  ],
  ultra: [
    'Everything in Reader',
    'Cloud sync across devices',
    'History export (CSV/JSON)',
    'Streak freezes (3 per month)',
    'Early access to new features',
  ],
};

export const UpsellPricing: React.FC<UpsellPricingProps> = ({ onNavigate }) => {
  const tiers: MembershipTier[] = ['starter', 'reader', 'ultra'];

  return (
    <div className="mb-16">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
          Choose Your Plan
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
          All plans include unlimited practice sessions. Cancel anytime.
        </p>
      </div>

      {/* Free tier banner */}
      <div className="mb-8 rounded-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="flex flex-col sm:flex-row items-center justify-between p-6 gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-slate-500 to-slate-600 p-3 rounded-xl shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white">Free</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Get started with 3 stories. No credit card required.</p>
            </div>
          </div>
          <button
            onClick={() => onNavigate('/signup')}
            className="py-3 px-6 rounded-xl font-semibold text-white bg-slate-600 hover:bg-slate-700 transition-colors whitespace-nowrap"
          >
            Get Started
          </button>
        </div>
      </div>

      {/* Paid tiers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {tiers.map((tier) => {
          const info = TIER_INFO[tier];
          const isReader = tier === 'reader';

          return (
            <div
              key={tier}
              className={`pricing-card relative p-6 rounded-2xl border shadow-sm flex flex-col ${
                isReader
                  ? 'bg-gradient-to-b from-blue-50 to-white dark:from-blue-900/30 dark:to-slate-800 border-blue-500 shadow-lg'
                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
              }`}
            >
              {isReader && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-xs font-semibold shadow-md">
                  Best Value
                </div>
              )}

              <div className="mb-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 rounded-xl ${tierIconBg[tier]} shadow-lg`}>
                    {tierIcons[tier]}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">{info.name}</h3>
                  </div>
                </div>
                <p className="text-3xl font-extrabold text-slate-900 dark:text-white mb-1">
                  ${TIER_PRICING[tier as keyof typeof TIER_PRICING]}
                  <span className="text-base font-normal text-slate-500">/mo</span>
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {tier === 'starter' && 'For casual readers'}
                  {tier === 'reader' && 'For dedicated readers'}
                  {tier === 'ultra' && 'For power readers'}
                </p>
              </div>

              <ul className="space-y-3 mb-6 flex-grow">
                {tierFeatures[tier].map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                    <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      tier === 'starter' ? 'text-green-500' : tier === 'reader' ? 'text-blue-500' : 'text-purple-500'
                    }`} />
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => onNavigate('/signup')}
                className={`w-full py-2.5 rounded-xl font-semibold text-white transition-colors ${
                  tier === 'starter'
                    ? 'bg-green-500 hover:bg-green-600'
                    : tier === 'reader'
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-md'
                }`}
              >
                Get {info.name}
              </button>
            </div>
          );
        })}
      </div>

      {/* Trust text */}
      <div className="text-center mt-8">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Cancel anytime · No hidden fees · Start in 30 seconds
        </p>
      </div>
    </div>
  );
};
